import { Platform } from '..'
import * as AccountGroup from '../account-group'
import { CurrencyCode, CurrencySymbol } from '../currency'
import { Domain } from '../domain/domain'
import { User } from '../user/user'
import { PerformanceMode } from '../targets'
import { LookbackWindow } from '../lookback-window'
import { ID } from './id'
import { ColourHexCode } from './colour'

/**
 * @deprecated Use `LookbackWindow` from '@opteo/types' instead.
 * This is a backwards-compatible alias that will be removed in a future version.
 */
export const MetricPeriod = {
    LAST_30_DAYS: LookbackWindow.LAST_30_DAYS,
    LAST_90_DAYS: LookbackWindow.LAST_90_DAYS,
    LAST_180_DAYS: LookbackWindow.LAST_180_DAYS,
    LAST_365_DAYS: LookbackWindow.LAST_365_DAYS,
    MONTH_TO_DATE: LookbackWindow.MONTH_TO_DATE,
} as const

/**
 * @deprecated Use `LookbackWindow` from '@opteo/types' instead.
 */
export type MetricPeriod = (typeof MetricPeriod)[keyof typeof MetricPeriod]

/**
 * @description Why Opteo can't reach an account any more. `connection`: every
 * link to it rides a broken OAuth connection, fixed by reconnecting the platform
 * in Opteo. `access`: the connections work but no longer reach this account
 * (e.g. removed from an MCC or Business Manager), fixed on the platform itself.
 * Unless access returns, the account is removed from Opteo at `removalAt` (ISO).
 */
export interface AccessLost {
    at: string
    reason: 'connection' | 'access'
    removalAt: string
}

/**
 * @description Data for an Account object
 */
export type Info = {
    name: string
    // User selected or default assigned hex code
    color: ColourHexCode
    // Identifier of Account platform e.g. Microsoft Ads
    platform: Platform.Platform
    // Platform currency
    currencyCode: CurrencyCode
    currencySymbol: CurrencySymbol
    // Minimum daily budget allowed (in account currency, converted from USD)
    minDailyBudget: number
    // Platform timezone
    timezone: string
    // Opteo improvement counts
    tasksAvailable: number
    tasksCompleted: number
    tasksDismissed: number
    // Opteo issue counts
    issuesActive: number
    issuesResolved: number
    issuesIgnored: number
    // Account is selected in Linked Accounts
    active: boolean
    // True when the requesting user is not connected (active === false) but
    // another team member still is. Distinguishes a "disconnected" account (live
    // elsewhere on the team) from an "unlinked" one (no team member connected).
    disconnected?: boolean
    // Set while Opteo can't reach the account (its data has stopped syncing)
    accessLost?: AccessLost | null
    // Last viewed by a user from the app
    lastVisitedAt: string | null
    // Email address used to link the account to Opteo
    connectionEmail?: string
    // Platform specific metadata, varies per platform
    // TODO: Consider types if needed e.g. type MicrosoftAdsPlatformMetadata = { ... }
    // TODO: Consider if this is needed (?)
    metadata: Record<string, unknown> | null

    // Account ID, useful when the account is a legacy domain and the ID is taken up by the domain ID
    // Should be removed once the great transition is complete.
    accountId: ID

    /**
     * Account group ID. Null if the account is ungroupped.
     */
    accountGroupId: AccountGroup.ID | null

    /**
     * User ID of the account manager. Null if the account is unassigned.
     */
    managerUserId: User.ID | null

    // Will be the CID (eg 123-123-1234) for Google accounts, and the "account number" (eg F132R7JS) for Microsoft accounts.
    idOnPlatform: string | number

    // Used to sort accountCentre. Set when listing, not set when getting single account.
    costUSD?: number
    conversionsAca?: number
    goalProgress?: number
    // CPA or ROAS, account-level.
    performanceMode: PerformanceMode

    /** Legacy domainId for google accounts - should be phased out */
    legacyId?: number | null

    // Account Setup has been seen
    accountSetupSeen: boolean

    accountGroup?: AccountGroup.Info
} & ({ id: ID; legacy: false } | { id: Domain.ID; legacy: true })

export type Metrics = {
    cost30days: number
    costPrevious30days: number
    revenue30days: number
    revenuePrevious30days: number
    costUSD30days: number
    conversionsPrevious30days: number
    conversions30days: number
    currentMonthCost: number
    conversionWeekly: SparklineData[]
    costWeekly: SparklineData[]
    revenueWeekly: SparklineData[]
}

export type SparklineData = { x: number; y: number }

export enum MetricType {
    SPEND = 'spend',
    CONVERSION = 'conversion',
    CONVERSION_VALUE = 'conversionValue',
    CPA = 'cpa',
    ROAS = 'roas',
}

export type MetricsV2 = {
    /**
     * Data points used to render the sparkline chart for this metric.
     * Includes both the current and previous periods to show the full trend.
     */
    points: SparklineData[]

    /**
     * Aggregated value of the metric over the selected time period.
     * Depending on the metric, this could represent a count or monetary amount (e.g. conversion vs spend).
     */
    total: number

    /**
     * Percentage change compared to the previous equivalent period.
     */
    delta: number
}

export enum BudgetStatus {
    FAR_OVER = 'far_over',
    OVER = 'over',
    GOOD = 'good',
    UNDER = 'under',
    FAR_UNDER = 'far_under',
    TOO_EARLY = 'too_early',
    INVALID = 'invalid',
}

export type BudgetBar = {
    /**
     * Total budget allocated for the current month
     */
    budget: number

    /**
     * Allowed percentage variance for budget tracking (e.g. 0.2 for 20%)
     */
    budgetVariance: number

    /**
     * Actual amount spent so far in the current month
     */
    actualSpend: number

    /**
     * Current budget status indicating how the actual spend compares to the ideal spend at this point in the month
     */
    status: BudgetStatus

    /**
     *  Whether the account has auto-pause enabled when budget is exceeded
     */
    hasBudgetAutoPause: boolean

    /**
     * Whether a budget has been set for the account
     */
    hasBudget: boolean

    /**
     * Percentage of the difference between actual and ideal spend
     */
    displayDifference: number

    /**
     * Percentage of the month that has passed (e.g. 50 for mid-month)
     */
    monthProgressPercentage: number

    /**
     *  Percentage of the budget that has been spent so far
     */
    monthSpentPercentage: number
}
